import { useState, useEffect } from "react";
import { Card, Button, Form, Table, Modal } from "react-bootstrap";
import Breadcrumb from "app/components/Breadcrumb";
import api from "app/services/api";

const STATUS_FILTERS = ["all", "pending", "approved", "rejected"];

// Pending items need attention first, then most recently inwarded.
const STATUS_ORDER = { pending: 0, approved: 1, rejected: 2 };

const qualityBadgeClass = (value) => {
  if (value === "approved") return "bg-success";
  if (value === "rejected") return "bg-danger";
  return "bg-warning text-dark";
};

export default function Quality() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [reviewEntry, setReviewEntry] = useState(null);
  const [reviewStatus, setReviewStatus] = useState("approved");
  const [reviewRemark, setReviewRemark] = useState("");

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = () => {
    setLoading(true);
    api
      .get("/api/inward", { withCredentials: true })
      .then((res) => {
        setEntries(res.data.inwardEntries || []);
      })
      .catch(() => {
        setEntries([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubmitReview = () => {
    api
      .patch(
        "/api/inward/" + reviewEntry.id + "/quality-check",
        { quality_check: reviewStatus, remark: reviewRemark },
        { withCredentials: true },
      )
      .then(() => {
        setReviewEntry(null);
        fetchEntries();
        setSuccessMsg("Quality status updated successfully");
        setTimeout(() => setSuccessMsg(""), 3000);
      })
      .catch((err) => {
        const data = err.response?.data;
        setErrorMsg(data?.message || "Failed to update quality status");
        setTimeout(() => setErrorMsg(""), 3000);
      });
  };

  const filteredEntries = entries
    .filter((item) => {
      const status = item.quality_check || "pending";
      if (statusFilter !== "all" && status !== statusFilter) return false;

      const term = search.toLowerCase();
      return (
        (item.product_name || "").toLowerCase().includes(term) ||
        (item.category_name || "").toLowerCase().includes(term)
      );
    })
    .sort((a, b) => {
      const rankA = STATUS_ORDER[a.quality_check || "pending"];
      const rankB = STATUS_ORDER[b.quality_check || "pending"];
      if (rankA !== rankB) return rankA - rankB;
      return new Date(b.created_at) - new Date(a.created_at);
    });

  const totalRows = filteredEntries.length;
  const totalPages = rowsPerPage >= totalRows ? 1 : Math.ceil(totalRows / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedEntries = filteredEntries.slice(startIndex, startIndex + rowsPerPage);

  const pendingCount = entries.filter(
    (item) => (item.quality_check || "pending") === "pending",
  ).length;

  return (
    <section>
      <Breadcrumb
        routeSegments={[
          { name: "Dashboard", path: "/" },
          { name: "Product Management", path: "/product" },
          { name: "Quality" },
        ]}
      />

      {successMsg && <div className="alert alert-success">{successMsg}</div>}
      {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}

      <Card body className="mb-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Card.Title className="mb-0">Quality Approval</Card.Title>
            <small className="text-muted">
              {pendingCount} item{pendingCount === 1 ? "" : "s"} awaiting review
            </small>
          </div>

          <div className="d-flex align-items-center">
            <Form.Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{ width: "150px", marginRight: "10px" }}
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option} value={option}>
                  {option === "all" ? "All Status" : option}
                </option>
              ))}
            </Form.Select>

            <input
              type="text"
              placeholder="Search Product / Category"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: "250px",
                height: "38px",
                paddingLeft: "10px",
                border: "1px solid #d1d1d1",
              }}
            />
          </div>
        </div>
      </Card>

      <Card body>
        <div style={{ overflowX: "auto" }}>
          <Table bordered hover className="align-middle">
            <thead style={{ backgroundColor: "#cfc7d9" }}>
              <tr>
                <th>No</th>
                <th>Product Name</th>
                <th>Category Name</th>
                <th>GRN No</th>
                <th>Invoice No</th>
                <th>Supplier</th>
                <th>Date</th>
                <th>Received Qty</th>
                <th>Price</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="11" className="text-center">Loading...</td>
                </tr>
              )}

              {!loading && paginatedEntries.length === 0 && (
                <tr>
                  <td colSpan="11" className="text-center">No items found.</td>
                </tr>
              )}

              {!loading &&
                paginatedEntries.map((item, index) => (
                  <tr key={item.id}>
                    <td>{startIndex + index + 1}</td>
                    <td>{item.product_name || "-"}</td>
                    <td>{item.category_name || "-"}</td>
                    <td>{item.grn_no || "-"}</td>
                    <td>{item.invoice_no || "-"}</td>
                    <td>{item.supplier_name || "-"}</td>
                    <td>
                      {item.inward_date
                        ? new Date(item.inward_date).toLocaleDateString("en-CA")
                        : item.created_at
                          ? new Date(item.created_at).toLocaleDateString("en-CA")
                          : "-"}
                    </td>
                    <td>{item.qty}</td>
                    <td>{Number(item.price || 0).toFixed(2)}</td>
                    <td>
                      <span className={"badge " + qualityBadgeClass(item.quality_check)}>
                        {item.quality_check || "pending"}
                      </span>
                    </td>
                    <td>
                      <Button
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          backgroundColor: "#663399",
                          border: "none",
                          width: "28px",
                          height: "28px",
                          padding: 0,
                        }}
                        title="Quality Review"
                        onClick={() => {
                          setReviewEntry(item);
                          setReviewStatus(
                            item.quality_check === "rejected" ? "rejected" : "approved",
                          );
                          setReviewRemark(item.remark || "");
                        }}
                      >
                        <i className="i-Gear text-white"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>

        <div className="d-flex justify-content-end align-items-center mt-3">
          <span className="me-2">Rows per page</span>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{ width: "90px", marginRight: "15px" }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={filteredEntries.length}>All</option>
          </Form.Select>

          <Button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Prev
          </Button>
          <span className="mx-3">
            {currentPage} / {totalPages || 1}
          </span>
          <Button
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      </Card>

      <Modal show={!!reviewEntry} onHide={() => setReviewEntry(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Quality Review</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Label>Product</Form.Label>
            <Form.Control
              type="text"
              value={reviewEntry?.product_name || ""}
              readOnly
              className="mb-3"
              style={{ backgroundColor: "#f8f9fa" }}
            />

            <Form.Label>Quantity Received</Form.Label>
            <Form.Control
              type="text"
              value={reviewEntry?.qty ?? ""}
              readOnly
              className="mb-3"
              style={{ backgroundColor: "#f8f9fa" }}
            />

            <Form.Label>Price</Form.Label>
            <Form.Control
              type="text"
              value={Number(reviewEntry?.price || 0).toFixed(2)}
              readOnly
              className="mb-3"
              style={{ backgroundColor: "#f8f9fa" }}
            />

            <Form.Label>Status</Form.Label>
            <Form.Select
              value={reviewStatus}
              onChange={(e) => setReviewStatus(e.target.value)}
            >
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </Form.Select>

            <Form.Label className="mt-3">Remark</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reviewRemark}
              onChange={(e) => setReviewRemark(e.target.value)}
              placeholder="Add a remark"
            />

            <small className="text-muted d-block mt-2">
              Approved items are added to stock. Rejected items are kept out of stock.
            </small>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setReviewEntry(null)}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: "#663399", border: "none" }}
            onClick={handleSubmitReview}
          >
            Submit Review
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}
